import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

const scopes = [
  {
    icon: "ri-file-list-3-line",
    title: "影片介绍",
    desc: "片名、年份、类型、片长、制片地区等基础资料，逐条核对后整理成统一格式。",
  },
  {
    icon: "ri-book-open-line",
    title: "剧情解析",
    desc: "从叙事结构、镜头语言与人物动机出发，说明一部作品为什么值得看、好看在哪里。",
  },
  {
    icon: "ri-team-line",
    title: "演员信息",
    desc: "导演与主要演员的署名整理，方便按主创线索继续查找同类作品。",
  },
  {
    icon: "ri-chat-quote-line",
    title: "影评推荐",
    desc: "汇集编辑短评与读者观点摘要，用不同视角补充单一介绍的不足。",
  },
];

export default function About() {
  usePageMeta(
    "关于我们 - 韩国三级电影资料站定位与版权说明",
    "韩国三级电影是一个影视资料与影评整理站点，专注影片介绍、剧情解析、主创信息与观众评论摘要，不提供在线播放与下载。",
    "关于韩国三级电影,影视资料站,版权声明,免责声明",
  );

  return (
    <SiteLayout>
      <div className="w-full border-b border-background-200/60 bg-background-100/40">
        <div className="mx-auto w-full max-w-[1360px] px-4 pb-10 pt-6 md:px-8 md:pb-14 md:pt-8">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-7">
              <span className="font-label text-[11px] uppercase tracking-[0.28em] text-accent-500">
                About us
              </span>
              <h1 className="mt-2 text-balance font-heading text-3xl font-black leading-tight tracking-tight text-foreground-950 md:text-[42px]">
                关于「韩国三级电影」
              </h1>
              <p className="mt-4 text-sm leading-8 text-foreground-600 md:text-[15px]">
                这是一个以韩国影视作品为主题的资料整理站点。我们关注的不是流量，而是一部片子背后可以被讲清楚的东西：
                它讲了什么故事，用什么方式讲，主创是谁，观众又从中看到了什么。
              </p>
              <p className="mt-3 text-sm leading-8 text-foreground-600 md:text-[15px]">
                站内所有条目采用统一的资料结构，方便横向对比与快速检索。我们不设播放器，也不提供任何下载入口，
                所有内容都围绕「介绍、解析与推荐」展开。
              </p>

              <div className="mt-7 grid grid-cols-3 gap-3">
                {[
                  { value: "1,980+", label: "资料条目" },
                  { value: "2010—2025", label: "收录跨年" },
                  { value: "4", label: "内容分类" },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="rounded-lg border border-background-200/80 bg-background-50 px-4 py-4"
                  >
                    <div className="font-display text-xl font-semibold text-accent-500 md:text-2xl">
                      {item.value}
                    </div>
                    <div className="mt-1 text-[11px] text-foreground-500">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative overflow-hidden rounded-lg border border-background-200/80">
                <div className="aspect-[4/3] w-full">
                  <img
                    src="https://readdy.ai/api/search-image?query=Cinematic%20editorial%20photograph%20of%20a%20quiet%20film%20archive%20room%2C%20shelves%20of%20vintage%20film%20reels%20and%20index%20cards%2C%20warm%20amber%20desk%20lamp%20light%20in%20deep%20crimson%20shadows%2C%20dust%20particles%20in%20the%20air%2C%20fine%20film%20grain%2C%20painterly%20photographic%20texture%2C%20no%20text%2C%20no%20lettering&width=900&height=675&seq=krfilm-about-studio&orientation=landscape"
                    alt="影视资料整理工作室场景"
                    title="韩国三级电影 资料整理与影评编辑"
                    className="h-full w-full object-cover object-center"
                  />
                </div>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-background-50 via-background-50/60 to-transparent p-4 pt-12">
                  <p className="text-xs text-foreground-600">
                    资料整理 · 影评编辑 · 分类索引
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1360px] px-4 py-12 md:px-8 md:py-16">
        <section>
          <div className="flex items-center gap-2">
            <span className="h-3 w-[3px] rounded-full bg-accent-500"></span>
            <span className="font-label text-[11px] uppercase tracking-[0.28em] text-accent-500">
              What we cover
            </span>
          </div>
          <h2 className="mt-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            内容范围
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-500">
            四个固定模块组成每一个影片条目，保证信息密度与可读性之间的平衡。
          </p>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {scopes.map((item) => (
              <div
                key={item.title}
                className="rounded-lg border border-background-200/80 bg-background-100/50 p-5"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-200 text-accent-400">
                  <i className={`${item.icon} text-lg`}></i>
                </span>
                <h3 className="mt-4 font-heading text-sm font-semibold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-xs leading-6 text-foreground-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-lg border border-background-200/80 bg-background-100/50 p-5 md:p-6">
            <div className="flex items-center gap-2">
              <i className="ri-copyright-line text-base text-primary-400"></i>
              <h2 className="font-heading text-base font-bold text-foreground-950">版权声明</h2>
            </div>
            <div className="mt-3 space-y-3 text-sm leading-7 text-foreground-600">
              <p>
                本站页面结构、文字整理与编辑评论均为原创内容，欢迎引用但请注明来源链接，请勿整站镜像或用于商业转售。
              </p>
              <p>
                影片名称、主创信息等事实性内容属于公共信息。站内海报与配图仅用于资料展示，相关权利归原权利人所有。
              </p>
              <p>
                如权利人认为某处内容使用不当，可通过站内反馈渠道说明具体页面与条目，我们会在核实后进行调整或移除。
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-primary-800/60 bg-primary-950/60 p-5 md:p-6">
            <div className="flex items-center gap-2">
              <i className="ri-error-warning-line text-base text-accent-400"></i>
              <h2 className="font-heading text-base font-bold text-primary-50">免责声明</h2>
            </div>
            <div className="mt-3 space-y-3 text-sm leading-7 text-primary-100/75">
              <p>
                本站不提供任何影视资源的在线播放、下载、分享或跳转服务，全部页面仅用于作品介绍与影评展示。
              </p>
              <p>
                站内影片资料、评分与评论均为演示用途的示例内容，不对应真实接口，也不构成对任何具体作品的推荐。
              </p>
              <p>
                本站内容面向成年观众。请读者自行判断浏览是否适合，并遵守所在地法律法规与平台规范。
              </p>
            </div>
          </div>
        </section>

        <section className="mt-12 rounded-lg border border-background-200/80 bg-background-100/40 p-6 md:p-8">
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                数据说明
              </h2>
              <p className="mt-2 text-sm leading-7 text-foreground-500">
                当前站点的影片数据为前端固定模拟数据，未接入真实数据库或第三方接口。
                搜索、筛选、排序与分页均在前端即时完成，关闭网络后除图片外仍可正常浏览。
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-home-4-line"></i>
                返回首页
              </Link>
              <Link
                to="/category"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-400"
              >
                浏览影片分类
              </Link>
            </div>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}